/**
 * Challenge logic — C1 canned misses; C2 explore + dig-chat Narrator beats (local).
 * On success the client calls POST /api/v1/game/complete for progress sync.
 * Riddler voice is marked by a green ? badge on the bubble (not trailing text).
 */

export const CHALLENGE_1 = {
  id: 1,
  answerNeedle: 'vowels',
  answerNeedles: ['vowels', 'aeiou'],
};

/** Check if the player's answer matches Challenge 1 - accepts "vowels" or all vowel letters a,e,i,o,u */
export function IsCorrectChallenge1(text) {
  if (!text) return false;
  const normalized = text.toLowerCase().replace(/\s/g, '');
  if (normalized.includes('tennis') || normalized.includes('court')) return false;
  if (normalized.includes('aeiou')) return true;
  const vowels = new Set(['a', 'e', 'i', 'o', 'u']);
  const letters = new Set(normalized.replace(/[^a-z]/g, ''));
  return vowels.size === 5 && [...vowels].every(v => letters.has(v));
}

export const NEGATIVE_REPLIES = [
  'Wrong! Riddle me this again, and try not to embarrass yourself.',
  'Ha! Cold as the Batcave in winter. Dig deeper.',
  'Is that your final answer? Because it is delightfully incorrect.',
  'Oh, please. Even Harley could do better, and she barely tries.',
  'No, no, no. Think smaller… everyday… hiding in plain sight.',
  'Incorrect! The court is laughing at you. Or perhaps I am.',
  "Nuh-uh. You're circling the answer like a confused bat.",
  'Nice try, for an amateur. Hint: there are five of us.',
  "Denied. Puzzle boxes don't open for guesses that limp.",
  'Alas, mortal. That is not it. Read the riddle again.',
  'So close… said no one ever. Try once more.',
];

/** Narrator beats while the player digs the closet laptop (chat still open). */
export const DIG_CHAT_NARRATOR = [
  '{username} mumbled something under their breath, and off in the distance, the Riddler throws a tantrum.',
  '{username} muttered at the laptop. Somewhere far off, the Riddler stomps, then blames Goon42 for the missing cameras. Again.',
  '{username} whispers a half-finished thought. The Riddler shrieks that Goon42 was supposed to watch this hallway.',
  '{username} talks to the screen like it owes them rent. Distant Riddler meltdown. Goon42 is somehow already the villain.',
  '{username} says something quiet and useless. The Riddler pitches a fit about Goon42 dropping off the camera feed.',
];

export function FormatDigChatNarrator(template, username) {
  const name = String(username || 'Someone').trim() || 'Someone';
  return String(template || '').split('{username}').join(name);
}

/** Placeholder Goon42 lines until the LLM room is live. */
export const GOON_STALLS = [
  'Not impressed.',
  "You're wasting cycles.",
  'Try harder. Or leave.',
  'The console already told me you were coming.',
];

/**
 * Funhouse hold music — Play song reveals lyric-by-lyric (paragraph/line beats).
 * First chat on the waitlist teases the opening lyric; Play paints the rest.
 */
const HOLD_MUSIC_CHORUS = [
  'Please hold, please hold, your number’s almost gold',
  'The marquee knows your fate, but it won’t be told',
  'Step, wait, step, wait: the Conga never ends',
  'One more riddle on repeat until the velvet bends',
];

export const HOLD_MUSIC_LINES = [
  // Verse 1
  'Standing in a line that circles back to doubt',
  'Velvet rope ahead, lukewarm riddle water in my cup',
  'NOW SEEING someone who is definitely not me',
  'Ticket stub melting in my anxious little hand',
  // Chorus
  ...HOLD_MUSIC_CHORUS,
  // Verse 2
  'Goon42 on break, stilts by the snack machine',
  'Snooze Lounge humming like a cheap dream',
  'I came for secrets, stayed for queue etiquette',
  'YAML confetti on the floor: dreams deferred, please hold',
  // Chorus
  ...HOLD_MUSIC_CHORUS,
  // Bridge
  'If patience is a puzzle, consider me unsolved',
  'The usher winks in question marks and says I’m evolving',
  'Behind me another player mouthing every word',
  'We are the choir of the almost-served',
  // Final chorus
  ...HOLD_MUSIC_CHORUS,
  '(fade out… please continue to hold…)',
];

/** After the medley, Riddler teases in a shuffled-then-ordered deck of 10. */
export const HOLD_MUSIC_RIDDLER_TAUNTS = [
  'Catchy, isn’t it? I wrote the twelfth chorus myself. Nobody asked.',
  'Don’t clap. Clapping renews the license for another verse.',
  'Your stub is melting faster than my patience. Impressive.',
  'I rate that performance a solid “please continue to hold.”',
  'If you hummed along, the Conga considers that binding consent.',
  'Encore? No. Suffer quietly like a professional guest.',
  'Somewhere a walk-in is Entering. Somewhere is not your number.',
  'The Snooze Lounge sends its regards. And a coupon for more fog.',
  'I invent new ways to mock ticket numbers as a hobby. Yours is… fine.',
  'Keep holding. I’ve got seventeen more bridges and a horn section on retainers.',
];

/** Opening teaser after the player chats in the waiting room. */
export function HoldMusicTeaserLine() {
  return `🎵 ${HOLD_MUSIC_LINES[0]} 🎵`;
}

/** Remaining lyrics as line-reveal units (`\\n\\n` = one beat per lyric). */
export function HoldMusicMedley(fromIndex = 1) {
  const lines = HOLD_MUSIC_LINES.slice(fromIndex);
  if (!lines.length) return '';
  return `🎵\n\n${lines.join('\n\n')}\n\n🎵`;
}

export function IsHoldMusicMedley(body) {
  const text = body || '';
  return text.includes('🎵') && text.includes('\n');
}

/** Soft PA nudges while a room is offered but not yet Enter'd. */
export const OFFER_HOLD_LINES = [
  '🎵 Please approach the velvet rope when ready. Enter is lit 🎵',
  '🎵 Your window is open. Goon42 is pretending not to watch the clock 🎵',
  '🎵 Two glittering minutes (give or take a musical number) 🎵',
  '🎵 The marquee knows your name. Hit Enter before intermission 🎵',
];

/**
 * Strip legacy trailing "?" signature from Riddler bodies (`.?` / ` ?` / `❔`).
 * Leaves natural mid-sentence or closing question marks alone when not glued to punctuation.
 */
export function StripLegacyRiddlerSignature(text) {
  let body = (text || '').replace(/\s+$/, '');
  if (body.endsWith('❔')) body = body.slice(0, -1).replace(/\s+$/, '');
  if (body.endsWith(' ?')) body = body.slice(0, -2).replace(/\s+$/, '');
  if (body.length >= 2 && body.endsWith('?') && '.!…'.includes(body[body.length - 2])) {
    body = body.slice(0, -1).replace(/\s+$/, '');
  }
  return body;
}

/** Fisher–Yates shuffle (copy). */
function Shuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Shuffle replies once, then return them in order (no repeats until the deck is gone).
 * When the deck is empty, reshuffle and start again.
 */
export function CreateReplyPicker(replies) {
  let deck = Shuffle(replies);
  let nextIndex = 0;

  return function PickReply() {
    if (nextIndex >= deck.length) {
      deck = Shuffle(replies);
      nextIndex = 0;
    }
    const line = deck[nextIndex];
    nextIndex += 1;
    return line;
  };
}

/** Play a fixed playlist in order, then loop from the top (hold music). */
export function CreateSequentialPicker(lines) {
  const playlist = [...lines];
  let nextIndex = 0;

  return function PickNextLine() {
    if (playlist.length === 0) return '';
    const line = playlist[nextIndex];
    nextIndex = (nextIndex + 1) % playlist.length;
    return line;
  };
}

export function CreateNegativeReplyPicker(replies = NEGATIVE_REPLIES) {
  return CreateReplyPicker(replies);
}

export function MakeLocalMessage(sender, body) {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    sender,
    body,
    created_at: new Date().toISOString(),
    local: true,
  };
}

export function MakeLocalRiddlerMessage(body) {
  return MakeLocalMessage('riddler', body);
}

export function MakeLocalNarratorMessage(body) {
  return MakeLocalMessage('narrator', body);
}

export function MakeLocalGoonMessage(body) {
  return MakeLocalMessage('goon', body);
}

export function BubbleRowClass(sender) {
  if (sender === 'user') return 'from-user';
  if (sender === 'narrator') return 'from-narrator';
  if (sender === 'goon') return 'from-goon';
  return 'from-riddler';
}

export function BubbleClass(sender) {
  if (sender === 'user') return 'user';
  if (sender === 'narrator') return 'narrator';
  if (sender === 'goon') return 'goon';
  return 'riddler';
}

export function SenderLabel(sender) {
  if (sender === 'narrator') return 'Narrator';
  if (sender === 'goon') return 'Goon42';
  return null;
}

/**
 * Narrators that should stay bright together.
 * A Narrator→Riddler/Goon→Narrator sandwich counts as one turn; consecutive
 * Narrators with no dialogue between them do not (older one dims).
 */
export function ActiveNarratorIndexSet(messages) {
  const active = new Set();
  if (!Array.isArray(messages) || messages.length === 0) return active;

  let lastNarrator = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.sender === 'narrator') {
      lastNarrator = i;
      break;
    }
  }
  if (lastNarrator < 0) return active;

  active.add(lastNarrator);
  let seenDialogue = false;
  for (let i = lastNarrator - 1; i >= 0; i -= 1) {
    const sender = messages[i]?.sender;
    if (sender === 'user') break;
    if (sender === 'riddler' || sender === 'goon') {
      seenDialogue = true;
      continue;
    }
    if (sender === 'narrator') {
      if (!seenDialogue) break;
      active.add(i);
      seenDialogue = false;
      continue;
    }
  }
  return active;
}

export function DisplayMessageBody(sender, body) {
  if (sender === 'riddler') return StripLegacyRiddlerSignature(body);
  return body;
}

/** Hide raw GitOps URLs that were previously inlined in Riddler copy. */
export function StripExposedGitopsUrl(body, gitopsUrl) {
  let text = body || '';
  if (gitopsUrl) {
    text = text.split(gitopsUrl).join('');
  }
  // Drop leftover bare github repo lines.
  text = text.replace(/^https?:\/\/\S+\s*$/gm, '');
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Split message text so the assigned room name can render as a link.
 * Returns an array of strings and { type:'room', name } tokens.
 */
export function MessageBodyTokens(body, roomName) {
  const text = body || '';
  if (!roomName || !text.includes(roomName)) {
    return [{ type: 'text', value: text }];
  }
  const parts = text.split(roomName);
  const tokens = [];
  parts.forEach((part, index) => {
    if (part) tokens.push({ type: 'text', value: part });
    if (index < parts.length - 1) {
      tokens.push({ type: 'room', name: roomName });
    }
  });
  return tokens;
}
